import type { Locale } from '../shared/locale';

export type Messages = {
  app: {
    muteSound: string;
    enableSound: string;
    loading: string;
    loadError: string;
    loadErrorHint: string;
    reload: string;
    back: string;
    map: string;
    menu: string;
    /** Inside-scene back pill: "Back to {name}" (name = parent scene). */
    backTo: string;
  };
  splash: {
    loadingAlt: string;
  };
  menu: {
    closeMenu: string;
    makkah: string;
    mapSubtitle: string;
    openMap: string;
    freeRoam: string;
    startJourney: string;
    settingsTitle: string;
    soundLabel: string;
    audioTitle: string;
    voiceLabel: string;
    musicLabel: string;
    themeLabel: string;
    allLocationsTitle: string;
    locationsTitle: string;
    sightIconAlt: string;
    /** Tabbed menu (Pilgrim 2.0): tab bar + per-tab progress header + scene cards. */
    tabHajj: string;
    tabHistorical: string;
    hajjProgressTitle: string;
    historicalProgressTitle: string;
    /** POI-based caption: "You have explored X out of Y points of interest". */
    poisProgress: string;
    spotsCount: string;
    exploreScene: string;
    mainMap: string;
    /** Inside-scene display names, keyed by canonical inside-scene id. */
    insideSceneLabels: Record<string, string>;
  };
  assistant: {
    initialMessage: string;
    assistantUnavailable: string;
    continuePrompt: string;
    defaultSightDescription: string;
    sightPrompt: string;
    moreDetailsLabel: string;
    moreDetailsPrompt: string;
    placeholder: string;
    openScene3D: string;
    exploreLocation: string;
    /** Short "Explore" label without the location name — used on the
     *  desktop intro overlay CTA where space allows a tighter button. */
    exploreShort: string;
    nextScene: string;
    status: {
      sending: string;
      thinking: string;
    };
    suggestions: {
      header: string;
      orbit: string;
      info: string;
      moreFacts: string;
      startJourney: string;
      continueStory: string;
      previous: string;
      next: string;
      pauseTour: string;
    };
  };
  chatInput: {
    clearMessage: string;
    openMediaGallery: string;
  };
  loadingText: {
    thinking: string;
  };
  photoViewer: {
    counterSeparator: string;
    prev: string;
    next: string;
    close: string;
    expand: string;
    collapse: string;
  };
  suggestions: {
    openMenu: string;
  };
  globalMap: {
    markerLabels: Record<string, string>;
  };
  overallOverlay: {
    /** "Map" — label for the welcome slide in the desktop side menu. */
    mapLabel: string;
    /** Keyboard hint shown below the desktop intro curtain. Two parts so
     *  the JSX can render a separator bullet between them. */
    keyboardHintArrows: string;
    keyboardHintAction: string;
    /** Long-form scene labels used for the slide TITLE on the overlay
     *  AND the desktop side-menu item label. Distinct from the short
     *  `globalMap.markerLabels` that the 3D POI markers use (those stay
     *  "Mina" / "Arafat", these are "Mina Valley" / "Arafat Mount"). */
    sceneLabels: {
      kaaba: string;
      mina: string;
      muzdalifah: string;
      arafat: string;
    };
  };
  /** Historical-scenes slider (clock button → centered era carousel). */
  historicalScenes: {
    /** aria-label for the clock button that opens the slider. */
    buttonLabel: string;
    /** aria-label for the slider close (×) button. */
    close: string;
    /** CTA label on each card. */
    exploreCta: string;
    /** Badge on a locked card — the era's scene isn't built yet. */
    comingSoon: string;
    /**
     * Per-era title + subtitle, keyed by `HistoricalSceneSlide.labelKey`
     * (e.g. `today`, `y1890s`). Year labels are placeholder content — the
     * content authority owns final copy (see PRODUCT_OVERVIEW §9).
     */
    eras: Record<string, { title: string; subtitle: string }>;
  };
  onboarding: {
    title: string;
    hint: string;
  };
  sceneTitle: {
    appName: string;
    journeyTo: (scene: string) => string;
  };
  devPanel: {
    title: string;
    sections: {
      locale: string;
      camera: string;
      orbitCenter: string;
      viewOffset: string;
      collision: string;
      devTools: string;
      cameraControls: string;
      pointsOfInterest: string;
      audio: string;
      appConfig: string;
    };
    locale: {
      label: string;
      options: Record<string, string>;
    };
    camera: Record<string, string>;
    hints: {
      camera: Record<string, string>;
      devTools: Record<string, string>;
      poi: Record<string, string>;
    };
    collision: Record<string, string>;
    devTools: Record<string, string>;
    poi: Record<string, string>;
    audio: Record<string, string>;
    musicTrackLabels: Record<string, string>;
    orbitDirection: Record<string, string>;
    viewMode: {
      label: string;
      options: Record<string, string>;
    };
    orbitCenter: Record<string, string>;
    viewOffset: Record<string, string>;
    resetLabel: string;
  };
};

export const messages: Record<Locale, Messages> = {
  en: {
    app: {
      muteSound: 'Mute sound',
      enableSound: 'Turn sound on',
      loading: 'Loading',
      loadError: 'We couldn’t load this scene',
      loadErrorHint: 'Please check your connection and try again.',
      reload: 'Reload',
      back: 'Back',
      map: 'Map',
      menu: 'Menu',
      backTo: 'Back to {name}',
    },
    splash: {
      loadingAlt: 'Loading…',
    },
    menu: {
      closeMenu: 'Close menu',
      makkah: 'Mekka Map',
      mapSubtitle:
        'Explore all sacred locations of the Hajj journey — Kaaba, Mina, Muzdalifah, Arafat',
      openMap: 'Open map',
      freeRoam: 'Free Roam Mode',
      startJourney: 'Start Hajj journey',
      settingsTitle: 'Settings',
      soundLabel: 'Sound',
      audioTitle: 'Audio',
      voiceLabel: 'Voice',
      musicLabel: 'Music',
      themeLabel: 'Dark theme',
      allLocationsTitle: 'All locations',
      locationsTitle: '{name} locations',
      sightIconAlt: '{name} icon',
      tabHajj: 'Hajj Journey',
      tabHistorical: 'Historical Scenes',
      hajjProgressTitle: 'Hajj Journey Progress',
      historicalProgressTitle: 'Historical Scenes Progress',
      poisProgress: 'You have explored {visited} out of {total} points of interest',
      spotsCount: '{visited} of {total} spots',
      exploreScene: 'Explore the scene',
      mainMap: 'Main map',
      insideSceneLabels: {
        'kaaba-inside': 'Inside the Kaaba',
      },
    },
    assistant: {
      initialMessage:
        "Hi! I'm your personal AI assistant. Ask me about any sight or ritual, and I will guide you.",
      assistantUnavailable: 'Could not reach the assistant.',
      continuePrompt: 'Please continue guiding me on this topic.',
      defaultSightDescription: '{name} is one of the notable sights here.',
      sightPrompt: 'Tell me about {name}.',
      moreDetailsLabel: 'More details',
      moreDetailsPrompt: 'Tell me more about {name}.',
      placeholder: 'Ask anything',
      openScene3D: 'Open 3D',
      exploreLocation: 'Explore {name}',
      exploreShort: 'Explore',
      nextScene: 'Next location – {name}',
      status: {
        sending: 'Sending',
        thinking: 'Thinking',
      },
      suggestions: {
        header: 'I can also tell you:',
        orbit: 'Orbit',
        info: 'Info',
        moreFacts: 'More facts',
        startJourney: 'Start Hajj journey',
        continueStory: 'Continue the story',
        previous: 'Previous',
        next: 'Next',
        pauseTour: 'Pause the tour',
      },
    },
    chatInput: {
      clearMessage: 'Clear message',
      openMediaGallery: 'Open media gallery',
    },
    loadingText: {
      thinking: 'Thinking...',
    },
    photoViewer: {
      counterSeparator: 'of',
      prev: 'Previous photo',
      next: 'Next photo',
      close: 'Close',
      expand: 'Expand description',
      collapse: 'Collapse description',
    },
    suggestions: {
      openMenu: 'Open menu',
    },
    globalMap: {
      markerLabels: {
        kaaba: 'The Kaaba',
        // Era scenes — drive the "Journey to …" preloader subtitle
        // (App.tsx looks up markerLabels[activeSceneId]).
        'kaaba-2000bce': 'The Kaaba 2000 B.C.E',
        'kaaba-600ce': 'The Kaaba 605 C.E',
        'kaaba-1890s': 'The Kaaba 1890s',
        'kaaba-2030': 'The Kaaba 2030s',
        meeqat: 'Meeqat',
        mina: 'Mina',
        muzdalifah: 'Muzdalifah',
        arafat: 'Arafat',
      },
    },
    overallOverlay: {
      mapLabel: 'Map',
      keyboardHintArrows: '←/→ arrows',
      keyboardHintAction: 'or click to navigate',
      sceneLabels: {
        kaaba: 'The Kaaba',
        mina: 'Mina Valley',
        muzdalifah: 'Muzdalifah',
        arafat: 'Arafat Mount',
      },
    },
    historicalScenes: {
      buttonLabel: 'Historical views',
      close: 'Close historical views',
      exploreCta: 'Explore',
      comingSoon: 'Coming soon',
      eras: {
        bce2000: { title: '2000 B.C.E', subtitle: 'Building the Kaaba' },
        y600: { title: '605', subtitle: 'The Kaaba before Muhammad' },
        y1890s: { title: '1890s', subtitle: 'The Late Ottoman Kaaba' },
        today: { title: 'Kaaba today', subtitle: "The sacred cube at Islam's holiest site" },
        y2030s: { title: '2030s', subtitle: 'Future of Makkah' },
      },
    },
    onboarding: {
      title: 'Explore the scene',
      hint: 'Rotate the scene with gestures and tap on the object markers to learn more.',
    },
    sceneTitle: {
      appName: 'Pilgrim',
      journeyTo: (scene: string) => `Journey to ${scene.replace(/^The /, 'the ')}`,
    },
    devPanel: {
      title: 'Pilgrim Config',
      sections: {
        locale: 'Localization',
        camera: 'Camera',
        orbitCenter: 'Orbit Center',
        viewOffset: 'View Offset',
        collision: 'Collision',
        devTools: 'Dev Tools',
        cameraControls: 'Camera Controls',
        pointsOfInterest: 'Points of Interest',
        audio: 'Audio',
        appConfig: 'App Config',
      },
      locale: {
        label: 'Language',
        options: {
          English: 'en',
          Arabic: 'ar',
        },
      },
      camera: {
        fov: 'Field of View',
        lookSpeedHorizontal: 'Look Speed X',
        lookSpeedVertical: 'Look Speed Y',
        dragInertia: 'Drag Inertia',
        movementSpeed: 'Move Speed',
        orbitAutoMove: 'Auto Orbit',
        orbitAutoMoveDirection: 'Orbit Direction',
        orbitAutoMoveSpeed: 'Orbit Speed',
        orbitMinDistance: 'Orbit Min Dist',
        orbitMaxDistance: 'Orbit Max Dist',
        orbitMinPolarAngle: 'Orbit Min Polar',
        orbitMaxPolarAngle: 'Orbit Max Polar',
        heightCoef: 'Height Mult',
        speedHeightMin: 'Min Height Spd',
        speedHeightMax: 'Max Height Spd',
        pinchMoveSpeed: 'Pinch Speed',
        verticalMin: 'Vertical Min',
        verticalMax: 'Vertical Max',
        clampMinHeight: 'Clamp Min H',
        clampMaxHeight: 'Clamp Max H',
      },
      hints: {
        camera: {
          orbitAutoMove: 'Continuously orbit around the target while in orbit view',
          orbitAutoMoveSpeed: 'Angular speed (radians/sec) when auto-orbit is enabled',
          orbitMinDistance: 'Closest camera distance allowed in orbit view',
          orbitMaxDistance: 'Farthest camera distance allowed in orbit view',
          orbitMinPolarAngle: 'Minimum vertical angle (radians) for orbit',
          orbitMaxPolarAngle: 'Maximum vertical angle (radians) for orbit',
        },
        devTools: {
          showReferenceGrid: 'Toggle the floor/ceiling guides that follow the clamp heights',
          showCollisionBounds: 'Outline all collision helpers (green AABBs + hidden FBX volumes)',
          showSightMarkers: 'Display magenta spheres for every sight anchor in the scene',
          showOrbitCenterMarker: 'Render a helper sphere at the orbit pivot to aid debugging',
          showTellerAssistant: 'Render the teller assistant companion model',
          showRearView: 'Display a rear-facing Spark render overlay on the camera',
        },
        poi: {
          showVisibleOnly: 'Only render POI markers if their sight is visible from the camera',
          occlusionProbeMaxOffset: 'Maximum distance to pull the occlusion probe toward the camera',
          occlusionProbeMinDistance: 'Closest allowed distance between the probe and the POI center',
        },
      },
      collision: {
        cameraRadius: 'Camera Radius',
        maxDistance: 'Raycast Range',
        epsilon: 'Epsilon',
      },
      devTools: {
        showReferenceGrid: 'Reference Grid',
        showCollisionBounds: 'Collision Bounds',
        showSightMarkers: 'Sight Markers',
        showOrbitCenterMarker: 'Orbit Center Marker',
        showTellerAssistant: 'Teller Assistant',
        showRearView: 'Rear View',
      },
      poi: {
        showVisibleOnly: 'Hide Occluded',
        occlusionProbeMaxOffset: 'Probe Offset Max',
        occlusionProbeMinDistance: 'Probe Distance Min',
      },
      audio: {
        soundOn: 'Sound On',
        musicOn: 'Music On',
        voiceOn: 'Voice On',
        voiceVolume: 'Voice Volume',
        assistantVoiceName: 'Assistant Voice',
        musicTrackId: 'Music Track',
        musicVolume: 'Music Volume',
      },
      musicTrackLabels: {
        'crowd-noise-284490': 'Crowd Noise',
        'generic-crowd-background-noise-31310': 'Background Crowd',
        'oud-c-arabian-400521': 'Oud C Arabian',
      },
      orbitDirection: {
        left: 'Left',
        right: 'Right',
      },
      viewMode: {
        label: 'View Mode',
        options: {
          'first-person': 'First-person',
          orbit: 'Orbit',
        },
      },
      orbitCenter: {
        x: 'Center X',
        y: 'Center Y',
        z: 'Center Z',
      },
      viewOffset: {
        enabled: 'Enabled',
        top: 'Top',
        right: 'Right',
        bottom: 'Bottom',
        left: 'Left',
      },
      resetLabel: 'Reset to Defaults',
    },
  },
  ar: {
    app: {
      muteSound: 'كتم الصوت',
      enableSound: 'تشغيل الصوت',
      loading: 'جارٍ التحميل',
      loadError: 'تعذّر تحميل هذا المشهد',
      loadErrorHint: 'يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
      reload: 'إعادة التحميل',
      back: 'رجوع',
      map: 'الخريطة',
      menu: 'القائمة',
      backTo: 'العودة إلى {name}',
    },
    splash: {
      loadingAlt: 'جارٍ التحميل…',
    },
    menu: {
      closeMenu: 'إغلاق القائمة',
      makkah: 'خريطة مكة',
      mapSubtitle:
        'بإمكانكم استكشاف جميع المواقع المقدسة في رحلة الحج: الكعبة، منى، مزدلفة، عرفات',
      openMap: 'فتح الخريطة',
      freeRoam: 'وضع التجوال الحر',
      startJourney: 'بدء رحلة الحج',
      settingsTitle: 'الإعدادات',
      soundLabel: 'الصوت',
      audioTitle: 'الصوتيات',
      voiceLabel: 'الصوت البشري',
      musicLabel: 'الموسيقى',
      themeLabel: 'الوضع الداكن',
      allLocationsTitle: 'جميع المواقع',
      locationsTitle: 'مواقع {name}',
      sightIconAlt: 'أيقونة {name}',
      tabHajj: 'رحلة الحج',
      tabHistorical: 'المشاهد التاريخية',
      hajjProgressTitle: 'تقدّم رحلة الحج',
      historicalProgressTitle: 'تقدّم المشاهد التاريخية',
      poisProgress: 'لقد استكشفت {visited} من {total} نقاط اهتمام',
      spotsCount: '{visited} من {total} نقاط',
      exploreScene: 'استكشف المشهد',
      mainMap: 'الخريطة الرئيسية',
      insideSceneLabels: {
        'kaaba-inside': 'داخل الكعبة',
      },
    },
    assistant: {
      initialMessage:
        'مرحبًا! أنا مساعدك الذكي. اسألني عن أي معلم أو شعيرة، وسأرشدك.',
      assistantUnavailable: 'تعذر الوصول إلى المساعد.',
      continuePrompt: 'يرجى مواصلة إرشادي في هذا الموضوع.',
      defaultSightDescription: '{name} من أبرز المعالم هنا.',
      sightPrompt: 'الهدي او الأضحية {name}.',
      moreDetailsLabel: 'مزيد من التفاصيل',
      moreDetailsPrompt: 'أخبرني المزيد عن {name}.',
      placeholder: 'اسأل أي شيء',
      openScene3D: 'افتح 3D',
      exploreLocation: 'استكشاف {name}',
      exploreShort: 'استكشاف',
      nextScene: 'الموقع التالي – {name}',
      status: {
        sending: 'جارٍ الإرسال',
        thinking: 'جارٍ التفكير',
      },
      suggestions: {
        header: 'يمكنني أيضًا أن أخبرك:',
        orbit: 'دوران',
        info: 'معلومات',
        moreFacts: 'مزيد من المعلومات',
        startJourney: 'بدء رحلة الحج',
        continueStory: 'متابعة القصة',
        previous: 'السابق',
        next: 'التالي',
        pauseTour: 'إيقاف الجولة مؤقتًا',
      },
    },
    chatInput: {
      clearMessage: 'مسح الرسالة',
      openMediaGallery: 'فتح معرض الوسائط',
    },
    loadingText: {
      thinking: 'جارٍ التفكير...',
    },
    photoViewer: {
      counterSeparator: 'من',
      prev: 'الصورة السابقة',
      next: 'الصورة التالية',
      close: 'إغلاق',
      expand: 'توسيع الوصف',
      collapse: 'طي الوصف',
    },
    suggestions: {
      openMenu: 'فتح القائمة',
    },
    globalMap: {
      markerLabels: {
        kaaba: 'الكعبة',
        'kaaba-2000bce': 'الكعبة 2000 ق.م.',
        'kaaba-600ce': 'الكعبة 605 م',
        'kaaba-1890s': 'الكعبة 1890',
        'kaaba-2030': 'الكعبة 2030',
        meeqat: 'الميقات',
        mina: 'منى',
        muzdalifah: 'مزدلفة',
        arafat: 'عرفات',
      },
    },
    overallOverlay: {
      mapLabel: 'خريطة',
      keyboardHintArrows: '←/→ الأسهم',
      keyboardHintAction: 'أو انقر للتنقل',
      sceneLabels: {
        kaaba: 'الكعبة',
        mina: 'وادي منى',
        muzdalifah: 'مزدلفة',
        arafat: 'جبل عرفة',
      },
    },
    historicalScenes: {
      buttonLabel: 'المشاهد التاريخية',
      close: 'إغلاق المشاهد التاريخية',
      exploreCta: 'استكشاف',
      comingSoon: 'قريبًا',
      eras: {
        bce2000: { title: '2000 ق.م.', subtitle: 'بناء الكعبة المشرفة' },
        y600: { title: '605', subtitle: 'الكعبة في عصر الجاهلية' },
        y1890s: { title: '1890', subtitle: 'الكعبة في أواخر العصر العثماني' },
        today: { title: 'الكعبة اليوم', subtitle: 'أقدس موقع الإسلام' },
        y2030s: { title: 'رؤية 2030', subtitle: 'مستقبل مكة المكرمة' },
      },
    },
    onboarding: {
      title: 'استكشف المشهد',
      hint: 'بإمكانك تدوير المشهد والنقر عليه واتباع التعليمات لمعرفة المزيد',
    },
    sceneTitle: {
      appName: 'الحاج',
      journeyTo: (scene: string) => `رحلة إلى ${scene}`,
    },
    devPanel: {
      title: 'إعدادات Pilgrim',
      sections: {
        locale: 'اللغة',
        camera: 'الكاميرا',
        orbitCenter: 'مركز الدوران',
        viewOffset: 'إزاحة العرض',
        collision: 'التصادم',
        devTools: 'أدوات التطوير',
        cameraControls: 'تحكمات الكاميرا',
        pointsOfInterest: 'نقاط الاهتمام',
        audio: 'الصوت',
        appConfig: 'إعدادات التطبيق',
      },
      locale: {
        label: 'اللغة',
        options: {
          'الإنجليزية': 'en',
          'العربية': 'ar',
        },
      },
      camera: {
        fov: 'زاوية الرؤية',
        lookSpeedHorizontal: 'سرعة النظر س',
        lookSpeedVertical: 'سرعة النظر ص',
        dragInertia: 'قصور السحب',
        movementSpeed: 'سرعة الحركة',
        orbitAutoMove: 'دوران تلقائي',
        orbitAutoMoveDirection: 'اتجاه الدوران',
        orbitAutoMoveSpeed: 'سرعة الدوران',
        orbitMinDistance: 'أقل مسافة دوران',
        orbitMaxDistance: 'أقصى مسافة دوران',
        orbitMinPolarAngle: 'أدنى زاوية قطبية',
        orbitMaxPolarAngle: 'أقصى زاوية قطبية',
        heightCoef: 'معامل الارتفاع',
        speedHeightMin: 'أدنى سرعة ارتفاع',
        speedHeightMax: 'أقصى سرعة ارتفاع',
        pinchMoveSpeed: 'سرعة القرص',
        verticalMin: 'أدنى عمودي',
        verticalMax: 'أقصى عمودي',
        clampMinHeight: 'أدنى ارتفاع مقيد',
        clampMaxHeight: 'أقصى ارتفاع مقيد',
      },
      hints: {
        camera: {
          orbitAutoMove: 'تدوير الهدف باستمرار في وضع المدار',
          orbitAutoMoveSpeed: 'السرعة الزاوية (راديان/ثانية) عند تفعيل الدوران التلقائي',
          orbitMinDistance: 'أقرب مسافة مسموحة في وضع المدار',
          orbitMaxDistance: 'أبعد مسافة مسموحة في وضع المدار',
          orbitMinPolarAngle: 'أدنى زاوية عمودية (راديان) للمدار',
          orbitMaxPolarAngle: 'أقصى زاوية عمودية (راديان) للمدار',
        },
        devTools: {
          showReferenceGrid: 'تبديل خطوط الأرض/السقف التي تتبع حدود الارتفاع',
          showCollisionBounds: 'إظهار حدود جميع أدوات التصادم (مربعات خضراء + مجسمات مخفية)',
          showSightMarkers: 'عرض كرات أرجوانية لكل معلم في المشهد',
          showOrbitCenterMarker: 'عرض كرة مساعدة عند محور الدوران للتصحيح',
          showTellerAssistant: 'عرض نموذج مساعد الراوي',
          showRearView: 'عرض طبقة عرض خلفية للكاميرا',
        },
        poi: {
          showVisibleOnly: 'عرض علامات المعالم فقط إذا كانت مرئية من الكاميرا',
          occlusionProbeMaxOffset: 'أقصى مسافة لسحب مسبار الحجب نحو الكاميرا',
          occlusionProbeMinDistance: 'أقرب مسافة مسموحة بين المسبار ومركز المعلم',
        },
      },
      collision: {
        cameraRadius: 'نصف قطر الكاميرا',
        maxDistance: 'مدى الأشعة',
        epsilon: 'إبسيلون',
      },
      devTools: {
        showReferenceGrid: 'شبكة مرجعية',
        showCollisionBounds: 'حدود التصادم',
        showSightMarkers: 'علامات المعالم',
        showOrbitCenterMarker: 'علامة مركز الدوران',
        showTellerAssistant: 'مساعد الراوي',
        showRearView: 'المنظر الخلفي',
      },
      poi: {
        showVisibleOnly: 'إخفاء المحجوب',
        occlusionProbeMaxOffset: 'أقصى إزاحة للمسبار',
        occlusionProbeMinDistance: 'أدنى مسافة للمسبار',
      },
      audio: {
        soundOn: 'تشغيل الصوت',
        musicOn: 'تشغيل الموسيقى',
        voiceOn: 'تشغيل الصوت البشري',
        voiceVolume: 'مستوى الصوت البشري',
        assistantVoiceName: 'صوت المساعد',
        musicTrackId: 'المسار الموسيقي',
        musicVolume: 'مستوى الموسيقى',
      },
      musicTrackLabels: {
        'crowd-noise-284490': 'ضجيج الحشود',
        'generic-crowd-background-noise-31310': 'ضجيج الخلفية',
        'oud-c-arabian-400521': 'عود عربي',
      },
      orbitDirection: {
        left: 'يسار',
        right: 'يمين',
      },
      viewMode: {
        label: 'نمط العرض',
        options: {
          'first-person': 'من منظور الشخص',
          orbit: 'مدار',
        },
      },
      orbitCenter: {
        x: 'المركز س',
        y: 'المركز ص',
        z: 'المركز ع',
      },
      viewOffset: {
        enabled: 'مفعل',
        top: 'أعلى',
        right: 'يمين',
        bottom: 'أسفل',
        left: 'يسار',
      },
      resetLabel: 'إعادة التعيين للافتراضي',
    },
  },
};
